import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { permissionService } from './permissionService'

/**
 * Pinia store for managing user permissions and related state
 */
export const usePermissionStore = defineStore('permission', () => {
  const userPermissions = ref<Set<string>>(new Set())
  const isLoadingPermissions = ref(false)
  const permissionError = ref<string | null>(null)

  const hasPermissions = computed(() => userPermissions.value.size > 0)

  const handleError = (error: unknown, defaultMsg: string) => {
    const msg = error instanceof Error ? error.message : defaultMsg
    permissionError.value = msg
    console.error(msg, error)
    return msg
  }

  const withLoading = async <T>(fn: () => Promise<T>): Promise<T> => {
    try {
      isLoadingPermissions.value = true
      permissionError.value = null
      return await fn()
    } catch (error) {
      handleError(error, 'Permission check failed')
      throw error
    } finally {
      isLoadingPermissions.value = false
    }
  }

  /**
   * Add granted permissions to the local set
   */
  const addPermissions = (codes: string[], permissions: boolean[]) => {
    codes.forEach((code, index) => {
      if (permissions[index]) userPermissions.value.add(code)
    })
  }

  /**
   * Check if the user is an Admin
   */
  async function isAdmin(): Promise<boolean> {
    try {
      return await withLoading(() => permissionService.isAdmin())
    } catch {
      return false
    }
  }

  /**
   * Check if the user has a specific permission
   */
  async function checkPermission(resourceCode: string): Promise<boolean> {
    try {
      return await withLoading(() => permissionService.hasPermission(resourceCode))
    } catch {
      return false
    }
  }

  /**
   * Check permissions for multiple resources and update local cache
   */
  async function checkPermissions(resourceCodes: string[]): Promise<boolean[]> {
    try {
      const permissions = await withLoading(() => permissionService.checkPermissions(resourceCodes))
      addPermissions(resourceCodes, permissions)
      return permissions
    } catch {
      return resourceCodes.map(() => false)
    }
  }

  /**
   * Check if the user has any of the specified permissions
   */
  async function hasAnyPermission(resourceCodes: string[]): Promise<boolean> {
    try {
      const result = await withLoading(() => permissionService.hasAnyPermission(resourceCodes))
      if (result) {
        const permissions = await permissionService.checkPermissions(resourceCodes)
        addPermissions(resourceCodes, permissions)
      }
      return result
    } catch {
      return false
    }
  }

  /**
   * Check if the user has all of the specified permissions
   */
  async function hasAllPermissions(resourceCodes: string[]): Promise<boolean> {
    try {
      const result = await withLoading(() => permissionService.hasAllPermissions(resourceCodes))
      if (result)
        addPermissions(
          resourceCodes,
          resourceCodes.map(() => true),
        )
      return result
    } catch {
      return false
    }
  }

  /**
   * Clear all permission cache and local state
   */
  function clearCache(): void {
    permissionService.clearCache()
    userPermissions.value.clear()
    permissionError.value = null
  }

  /**
   * Clear cache for a specific resource
   */
  function clearCacheForResource(resourceCode: string): void {
    permissionService.clearCacheForResource(resourceCode)
    userPermissions.value.delete(resourceCode)
  }

  return {
    userPermissions,
    isLoadingPermissions,
    permissionError,
    hasPermissions,
    isAdmin,
    checkPermission,
    checkPermissions,
    hasAnyPermission,
    hasAllPermissions,
    clearCache,
    clearCacheForResource,
    getCacheSize: () => permissionService.getCacheSize(),
  }
})
